// Lib
import { countBy, map, min, max, clamp, flow, constant, identity } from 'lodash/fp';
import { reduce } from 'lodash';

// Utils
import { getBottom, getLeft, getRight, getTop } from '../../../../../../../common/maths/geometry/rect';
import { createLayoutAction, GetDistributedMovesFn } from '../arrangementUtils';

// Constants
import { AlignmentSide } from './alignmentConstants';
import { ElementMeasurement } from '../../../../../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';

// This file operates on "rects" a lot but in actual usage they're ElementMeasurements.
// Aliased here for brevity.
type Rect = ElementMeasurement;
type RectsToNumber = (rects: Rect[]) => number;

// Math utils
const alwaysZero = constant(0);
const clampPositive = clamp(0, Infinity);
const getMinValue = (prop: string): RectsToNumber => flow(map(prop), min, clampPositive);
const getMaxValue = (prop: string): RectsToNumber => flow(map(prop), max, clampPositive);
const getCenter = (valA: number, valB: number) => (valA + valB) / 2;

// Individual rectangle update functions
type RectUpdater = (val: number) => (rect: Rect) => Rect;
const shiftToLeft: RectUpdater = (val) => (rect) => ({ ...rect, x: val, left: val, right: val + rect.width });
const shiftToRight: RectUpdater = (val) => (rect) => ({
    ...rect,
    x: val - rect.width,
    right: val,
    left: val - rect.width,
});
const shiftToTop: RectUpdater = (val) => (rect) => ({ ...rect, y: val, top: val, bottom: val + rect.height });
const shiftToBottom: RectUpdater = (val) => (rect) => ({
    ...rect,
    y: val - rect.height,
    bottom: val,
    top: val - rect.height,
});
const shiftToHorizontalCenter: RectUpdater = (val) => (rect) => ({
    ...rect,
    x: val - getCenter(0, rect.width),
    left: val - getCenter(0, rect.width),
    right: val + getCenter(0, rect.width),
});
const shiftToVerticalCenter: RectUpdater = (val) => (rect) => ({
    ...rect,
    y: val - getCenter(0, rect.height),
    top: val - getCenter(0, rect.height),
    bottom: val + getCenter(0, rect.height),
});

// Value getters
const getHorizontalCenter = (rect: Rect) => getCenter(getLeft(rect), getRight(rect));
const getVerticalCenter = (rect: Rect) => getCenter(getTop(rect), getBottom(rect));

// Min possible value getters
const getMinPossibleLeft = alwaysZero;
const getMinPossibleTop = alwaysZero;
// Min possible right is the max width of any element
const getMinPossibleRight = getMaxValue('width');
const getMinPossibleBottom = getMaxValue('height');
// Min center point is half of the max value
const getMinPossibleHorizontalCenter: RectsToNumber = (rects) => getCenter(0, getMinPossibleRight(rects));
const getMinPossibleVerticalCenter: RectsToNumber = (rects) => getCenter(0, getMinPossibleBottom(rects));

// Min value getters
const getMinLeft = getMinValue('left');
const getMaxRight = getMaxValue('right');
const getMinTop = getMinValue('top');
const getMaxBottom = getMaxValue('bottom');
const getRectsHorizontalCenter: RectsToNumber = (rects) => getCenter(getMinLeft(rects), getMaxRight(rects));
const getRectsVerticalCenter: RectsToNumber = (rects) => getCenter(getMinTop(rects), getMaxBottom(rects));

const getMostCommon = (values: number[], fallback: number): number => {
    const counts: Record<string, number> = countBy(identity, values);

    // @ts-ignore-next-line reduce complains when we pass a record (it expects an array) but it works fine
    const mostCommonEntry = reduce(
        counts,
        (acc: { key: number; count: number }, count: number, keyString: string) => {
            // If count is less than current max, skip
            if (count < acc.count) return acc;

            const key = parseInt(keyString, 10);

            // If count is greater than current max, use it
            if (count > acc.count) return { key, count };

            // If the counts match, use the one that's closer to the fallback value
            return Math.abs(key - fallback) < Math.abs(acc.key - fallback) ? { key, count } : acc;
        },
        { key: fallback, count: 0 },
    );

    return mostCommonEntry.key;
};

type AlignRectsParams = {
    getMinPossibleVal: RectsToNumber;
    getFallbackVal: RectsToNumber;
    getCurrentRectVal: (rect: Rect) => number;
    alignTo: RectUpdater;
};

const alignRects =
    ({
        // Takes in rects and returns the min possible
        getMinPossibleVal,
        // Takes in rects and returns the value to use if there's no common value
        getFallbackVal,
        // Gets the current value for each rect
        getCurrentRectVal,
        // Updates the rect to the specified position
        alignTo,
    }: AlignRectsParams) =>
    (rects: Rect[]): Rect[] => {
        // Find minimum possible value
        const minVal = getMinPossibleVal(rects);

        // Find fallback value
        const fallbackVal = getFallbackVal(rects);

        // Find most common value, use fallback if necessary
        const values = rects.map(getCurrentRectVal);
        const mostCommonValue = getMostCommon(values, fallbackVal);

        // Use the max of the minimum and most common value
        const alignToValue = Math.max(minVal, mostCommonValue);

        // Update each rect to the new location
        return rects.map(alignTo(alignToValue));
    };

const alignRectsToLeft = alignRects({
    getMinPossibleVal: getMinPossibleLeft,
    getFallbackVal: getMinLeft,
    getCurrentRectVal: getLeft,
    alignTo: shiftToLeft,
});
const alignRectsToRight = alignRects({
    getMinPossibleVal: getMinPossibleRight,
    getFallbackVal: getMaxRight,
    getCurrentRectVal: getRight,
    alignTo: shiftToRight,
});
const alignRectsToHorizontalCenter = alignRects({
    getMinPossibleVal: getMinPossibleHorizontalCenter,
    getFallbackVal: getRectsHorizontalCenter,
    getCurrentRectVal: getHorizontalCenter,
    alignTo: shiftToHorizontalCenter,
});
const alignRectsToTop = alignRects({
    getMinPossibleVal: getMinPossibleTop,
    getFallbackVal: getMinTop,
    getCurrentRectVal: getTop,
    alignTo: shiftToTop,
});
const alignRectsToBottom = alignRects({
    getMinPossibleVal: getMinPossibleBottom,
    getFallbackVal: getMaxBottom,
    getCurrentRectVal: getBottom,
    alignTo: shiftToBottom,
});
const alignRectsToVerticalCenter = alignRects({
    getMinPossibleVal: getMinPossibleVerticalCenter,
    getFallbackVal: getRectsVerticalCenter,
    getCurrentRectVal: getVerticalCenter,
    alignTo: shiftToVerticalCenter,
});

const ALIGNMENT_FUNCTION_MAP: { [key in AlignmentSide]: GetDistributedMovesFn } = {
    [AlignmentSide.LEFT]: createLayoutAction(alignRectsToLeft),
    [AlignmentSide.RIGHT]: createLayoutAction(alignRectsToRight),
    [AlignmentSide.CENTER_HORIZ]: createLayoutAction(alignRectsToHorizontalCenter),
    [AlignmentSide.TOP]: createLayoutAction(alignRectsToTop),
    [AlignmentSide.BOTTOM]: createLayoutAction(alignRectsToBottom),
    [AlignmentSide.CENTER_VERT]: createLayoutAction(alignRectsToVerticalCenter),
};

export const getElementAlignmentMoves = (alignSide: AlignmentSide) => ALIGNMENT_FUNCTION_MAP[alignSide];
